import * as THREE from 'three';

import {
  type LabSceneFactory,
  createBeaker,
  createHotPlate,
  createLabStage,
  createRippleSurface,
  labGlassMaterial,
  refractiveLiquidMaterial,
  seeded,
} from './labScene3d';

/** What the 3D beaker shows. Every value comes from `calculateCrystallisation`. */
export interface SolutionsSceneState {
  /** Copper(II) salts give a deep blue solution; the reviewed curves today are colourless salts. */
  appearance: 'colourless' | 'blue';
  /** Water mass as a share of the drawn beaker (0–1); sets the liquid level. */
  waterFill: number;
  temperatureC: number;
  /** True while the hot plate keeps the solution hot; false after cooling. */
  heating: boolean;
  /** Faceted crystals that formed on cooling, in grams; the number of crystals follows it. */
  crystalsG: number;
  /** Solid that never dissolved, in grams; drawn as small grains on the floor. */
  undissolvedG: number;
}

// World units; a teaching view of one beaker, not to scale.
const FLOOR_Y = -1.26;
const H = 3.2;
const FULL_LIQUID = 2.75;
const MAX_CRYSTALS = 120;
const G_PER_CRYSTAL = 2;
const MAX_GRAINS = 260;
const G_PER_GRAIN = 1.5;
const THERMO = { x: 0.82, z: 0.35, tilt: -0.2, length: 3.9, bottomY: FLOOR_Y + 0.3 } as const;

/** Instances to draw for a mass of solid; at least one when any solid is present. */
export function solidCount(grams: number, gramsEach: number, max: number): number {
  if (!(grams > 0.05)) return 0;
  return Math.min(max, Math.max(1, Math.round(grams / gramsEach)));
}

export const SOLUTIONS_PREVIEW_STATES: Record<string, SolutionsSceneState> = {
  'hot-unsaturated': { appearance: 'colourless', waterFill: 100 / 600, temperatureC: 60, heating: true, crystalsG: 0, undissolvedG: 0 },
  'cooled-crystals': { appearance: 'colourless', waterFill: 100 / 600, temperatureC: 20, heating: false, crystalsG: 48.4, undissolvedG: 0 },
  'hot-excess-solid': { appearance: 'colourless', waterFill: 50 / 600, temperatureC: 40, heating: true, crystalsG: 0, undissolvedG: 268 },
  'large-water-many-crystals': { appearance: 'colourless', waterFill: 500 / 600, temperatureC: 0, heating: false, crystalsG: 233.5, undissolvedG: 0 },
  'boiling-hot-small-sample': { appearance: 'colourless', waterFill: 50 / 600, temperatureC: 100, heating: true, crystalsG: 0, undissolvedG: 0 },
  'blue-copper-sulfate-style': { appearance: 'blue', waterFill: 200 / 600, temperatureC: 20, heating: false, crystalsG: 30, undissolvedG: 0 },
};

export const createSolutionsScene: LabSceneFactory<SolutionsSceneState> = (renderer, palette, reducedMotion) => {
  const stage = createLabStage(renderer, palette, reducedMotion);
  const { scene, keep } = stage;
  const plate = createHotPlate(stage, FLOOR_Y);
  const beaker = createBeaker(stage, { floorY: FLOOR_Y, height: H, marks: 6 });
  const INNER_R = beaker.innerRadius;
  const INNER_FLOOR = beaker.innerFloorY;

  // Liquid: refractive. Colourless salts give a pale, almost clear solution; copper(II) a deep blue.
  // Same look as the reviewed water beaker: a colourless solution reads as clear water.
  const paleMaterial = refractiveLiquidMaterial(stage, palette.liquid);
  const blueMaterial = refractiveLiquidMaterial(stage, palette.liquid, { attenuationDistance: 1.1, tint: 0.15 });
  const body = new THREE.Mesh(keep(new THREE.CylinderGeometry(INNER_R, INNER_R, 1, 96, 1, true)), paleMaterial);
  const floor = new THREE.Mesh(keep(new THREE.CircleGeometry(INNER_R, 96)), paleMaterial);
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = INNER_FLOOR + 0.002;
  scene.add(body, floor);
  const surface = createRippleSurface(stage, INNER_R, paleMaterial, 16, 72);

  // Solids are never transmissive: refractive liquid cannot show other refractive objects.
  const crystalColour = (blue: boolean) =>
    blue ? palette.liquid.clone().lerp(palette.glass, 0.12) : palette.steam.clone().lerp(palette.glass, 0.25);
  const crystalMaterial = keep(new THREE.MeshPhysicalMaterial({
    color: crystalColour(false), roughness: 0.12, metalness: 0, clearcoat: 1, clearcoatRoughness: 0.08, envMapIntensity: 1.6, flatShading: true,
  }));
  const crystals = new THREE.InstancedMesh(keep(new THREE.OctahedronGeometry(0.11, 0)), crystalMaterial, MAX_CRYSTALS);
  crystals.castShadow = true;
  scene.add(crystals);
  const grainMaterial = keep(new THREE.MeshPhysicalMaterial({
    color: crystalColour(false).lerp(palette.bench, 0.25), roughness: 0.55, metalness: 0, flatShading: true,
  }));
  const grains = new THREE.InstancedMesh(keep(new THREE.IcosahedronGeometry(0.05, 0)), grainMaterial, MAX_GRAINS);
  grains.castShadow = true;
  scene.add(grains);

  // Thermometer: glass tube, bulb and a column that follows the model temperature (0–100 °C).
  const thermo = new THREE.Group();
  thermo.position.set(THERMO.x, THERMO.bottomY, THERMO.z);
  thermo.rotation.z = THERMO.tilt;
  const tubeMaterial = labGlassMaterial(stage, 0.35);
  tubeMaterial.side = THREE.DoubleSide;
  const tube = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.09, 0.09, THERMO.length, 24)), tubeMaterial);
  tube.position.y = THERMO.length / 2;
  tube.renderOrder = 12;
  const columnMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.accent, roughness: 0.2, clearcoat: 1, emissive: palette.accent, emissiveIntensity: 0.35 }));
  const bulb = new THREE.Mesh(keep(new THREE.SphereGeometry(0.14, 20, 14)), columnMaterial);
  const column = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.045, 0.045, 1, 12)), columnMaterial);
  thermo.add(tube, bulb, column);
  scene.add(thermo);

  stage.finish();

  const matrix = new THREE.Matrix4();
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const euler = new THREE.Euler();
  const scale = new THREE.Vector3();
  const hidden = new THREE.Matrix4().makeScale(0, 0, 0);
  let lastAppearance: SolutionsSceneState['appearance'] = 'colourless';

  /** A low mound on the beaker floor; index sets a fixed spot so the pile grows without reshuffling. */
  const pileSpot = (index: number, count: number, salt: number, lift: number) => {
    const pileR = Math.min(INNER_R - 0.16, 0.3 + 0.075 * Math.sqrt(count));
    const r = pileR * Math.sqrt((index + 0.5) / Math.max(count, 1));
    const angle = index * 2.39996 + seeded(index, salt) * 0.6;
    const mound = (0.04 + 0.018 * Math.sqrt(count)) * (1 - (r / pileR) ** 2);
    position.set(Math.cos(angle) * r - 0.25, INNER_FLOOR + lift + mound * seeded(index, salt + 1), Math.sin(angle) * r);
  };

  return {
    update: (state, t, playing) => {
      const blue = state.appearance === 'blue';
      if (state.appearance !== lastAppearance) {
        lastAppearance = state.appearance;
        const liquid = blue ? blueMaterial : paleMaterial;
        body.material = liquid;
        floor.material = liquid;
        surface.mesh.material = liquid;
        crystalMaterial.color.copy(crystalColour(blue));
        grainMaterial.color.copy(crystalColour(blue).lerp(palette.bench, 0.25));
      }

      const temperature = THREE.MathUtils.clamp(state.temperatureC, 0, 100);
      plate.setHeat(state.heating ? THREE.MathUtils.clamp((temperature - 15) / 85, 0, 1) : 0);

      const liquidHeight = THREE.MathUtils.clamp(state.waterFill, 0.02, 1) * FULL_LIQUID;
      body.scale.y = liquidHeight;
      body.position.y = INNER_FLOOR + liquidHeight / 2;
      surface.mesh.position.y = INNER_FLOOR + liquidHeight;
      surface.update(state.heating && temperature > 70 ? 0.012 : 0.004, t, false);

      const grainCount = solidCount(state.undissolvedG, G_PER_GRAIN, MAX_GRAINS);
      for (let index = 0; index < MAX_GRAINS; index += 1) {
        if (index >= grainCount) {
          grains.setMatrixAt(index, hidden);
          continue;
        }
        pileSpot(index, grainCount, 3, 0.04);
        euler.set(seeded(index, 4) * Math.PI, seeded(index, 5) * Math.PI, 0);
        grains.setMatrixAt(index, matrix.compose(position, quaternion.setFromEuler(euler), scale.setScalar(0.8 + seeded(index, 6) * 0.5)));
      }
      grains.instanceMatrix.needsUpdate = true;

      const crystalCount = solidCount(state.crystalsG, G_PER_CRYSTAL, MAX_CRYSTALS);
      // Above the instance cap the crystals grow instead, so a larger mass still looks larger.
      const growth = Math.cbrt(Math.max(1, state.crystalsG / (MAX_CRYSTALS * G_PER_CRYSTAL)));
      const crystalLift = grainCount > 0 ? 0.12 : 0.08;
      for (let index = 0; index < MAX_CRYSTALS; index += 1) {
        if (index >= crystalCount) {
          crystals.setMatrixAt(index, hidden);
          continue;
        }
        pileSpot(index, crystalCount, 7, crystalLift);
        euler.set((seeded(index, 8) - 0.5) * 1.2, seeded(index, 9) * Math.PI, (seeded(index, 10) - 0.5) * 1.2);
        const size = (0.75 + seeded(index, 11) * 0.6) * growth;
        scale.set(size, size * (1.2 + seeded(index, 12) * 0.5), size);
        crystals.setMatrixAt(index, matrix.compose(position, quaternion.setFromEuler(euler), scale));
      }
      crystals.instanceMatrix.needsUpdate = true;

      const columnLength = 0.15 + (temperature / 100) * (THERMO.length - 0.5);
      column.scale.y = columnLength;
      column.position.y = columnLength / 2;

      stage.render(playing);
    },
    resize: stage.resize,
    dispose: stage.dispose,
  };
};
